import React, { useEffect, useMemo, useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { useSnackbar } from 'notistack';
import strings from '../localization';
import { useWallet } from '../utils/wallet';
import { useConnection } from '../utils/connection';
import PairingDialog from '../components/DomiHealth/PairingDialog';
import DomiHealthApi, {
  DomiHealthApiError,
  IncorrectPassphraseError,
  LockedError,
} from '../services/domiHealth/api';
import { clearPairingBundle, getPairingBundle, savePairingBundle } from '../services/domiHealth/pairingStorage';
import {
  FlaggedLabValue,
  HealthRecord,
  PairingBundle,
  PatientInfo,
  RecordContent,
} from '../services/domiHealth/types';
import { coSignAndSubmit, deriveBackupSignatureHex, deriveUnlockPassphrase } from '../services/domiHealth/walletBridge';
import { playSynthesizedAudio, RecordedAudio, VoiceRecording } from '../services/domiHealth/voiceRecorder';
import { pickFileForUpload } from '../services/domiHealth/filePicker';
import { saveRecordContentToDevice } from '../services/domiHealth/fileDownload';

// `navigator.camera` (cordova-plugin-camera) has no first-party TS types
// in this project; declared narrowly, here only, rather than adding an
// ambient global type for the whole app over one call site.
declare const Camera: { DestinationType: { FILE_URI: number }; PictureSourceType: { CAMERA: number } };

function takePicture(): Promise<string> {
  return new Promise((resolve, reject) => {
    const camera = (navigator as any).camera;
    if (!camera) {
      reject(new Error('Camera is not available (cordova-plugin-camera not present — native app only).'));
      return;
    }
    camera.getPicture((fileUri: string) => resolve(fileUri), (err: any) => reject(err), {
      quality: 70,
      destinationType: Camera.DestinationType.FILE_URI,
      sourceType: Camera.PictureSourceType.CAMERA,
      correctOrientation: true,
    });
  });
}

function describeError(err: unknown): string {
  if (err instanceof IncorrectPassphraseError) {
    return strings.domiHealth.incorrectPassphrase;
  }
  if (err instanceof LockedError) {
    return strings.domiHealth.incorrectPassphrase; // session expired/never unlocked — same recovery: unlock again
  }
  if (err instanceof DomiHealthApiError && err.status === 409) {
    // The edge node's duplicate-upload rejection (collection.
    // DuplicateDocumentError) -- a friendly, specific message instead
    // of the generic "something went wrong" wrapper below, since this
    // isn't a failure at all from the user's perspective: the file is
    // already there.
    return strings.domiHealth.documentAlreadyUploaded;
  }
  const message = err instanceof Error ? err.message : String(err);
  return strings.formatString(strings.domiHealth.genericError, message) as string;
}

function hasFileContent(record: HealthRecord): boolean {
  // Regression (2026-09-2x): a listing response ALWAYS strips
  // data_base64 (see service.py's _record_to_json -- "never inline the
  // actual bytes in a listing response"), so checking for it here was
  // always false for every record the client actually has, including
  // real documents -- this broke downloading entirely, not just the
  // new lab-value rows it was meant to exclude.
  //
  // content_type is only ever set on an actual uploaded document's
  // payload (collection.import_document always sets it); a derived
  // per-lab-value record (source_record_id set, no file at all) never
  // has it -- this is the real, listing-safe signal for "this record
  // has fetchable file content".
  return 'content_type' in record.payload;
}

function formatRecord(record: HealthRecord): string {
  if (hasFileContent(record)) {
    const { content_type, size_bytes } = record.payload;
    return `${content_type ?? 'document'}, ${size_bytes ?? '?'} bytes`;
  }
  if (record.metric_type === 'grocery_item' || record.metric_type === 'restaurant_item') {
    const { quantity, total_price, unit_price } = record.payload;
    const price = total_price ?? unit_price;
    const priceText = typeof price === 'number' ? ` — $${price.toFixed(2)}` : '';
    return `${quantity ?? 1}x${priceText}`;
  }
  const { value, unit } = record.payload;
  return `${value ?? ''}${unit ? ` ${unit}` : ''}`;
}

function formatPatientInfo(patientInfo: PatientInfo): string {
  const parts = [patientInfo.name, patientInfo.date_of_birth, patientInfo.sex].filter(Boolean);
  return parts.join(' · ');
}

type DocumentType = 'lab' | 'grocery' | 'restaurant';

/** Maps the selected document type to the metric_type an upload should
 * actually send -- 'lab' keeps sending the same 'photo'/'document'
 * values every upload already used before receipts existed, so
 * selecting it (the default) changes nothing about existing behavior.
 * 'kind' distinguishes the two upload entry points (camera vs file
 * picker), matching collection.py's document_type dispatch on the
 * server (see process_document's docstring). */
function metricTypeForUpload(documentType: DocumentType, kind: 'photo' | 'document'): string {
  if (documentType === 'grocery') return 'grocery_receipt';
  if (documentType === 'restaurant') return 'restaurant_receipt';
  return kind;
}

function uploadedMessageForDocumentType(documentType: DocumentType): string {
  if (documentType === 'grocery') return strings.domiHealth.groceryReceiptUploaded;
  if (documentType === 'restaurant') return strings.domiHealth.restaurantReceiptUploaded;
  return strings.domiHealth.documentUploaded;
}

export default function DomiHealthPage() {
  const wallet = useWallet();
  const connection = useConnection();
  const { enqueueSnackbar } = useSnackbar();
  const userId = wallet?.publicKey?.toString() ?? '';

  const [pairingBundle, setPairingBundle] = useState<PairingBundle | null>(null);
  const [pairingDialogOpen, setPairingDialogOpen] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  // Only ever shown as a fallback -- see the auto-unlock effect below.
  // Not the primary unlock path any more (deriveUnlockPassphrase is),
  // kept only so an account encrypted before this change (with a
  // user-typed passphrase the wallet has no way to reproduce) doesn't
  // get permanently stranded.
  const [manualPassphrase, setManualPassphrase] = useState('');
  const [autoUnlocking, setAutoUnlocking] = useState(false);
  const [autoUnlockFailed, setAutoUnlockFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [records, setRecords] = useState<HealthRecord[]>([]);
  const [metricType, setMetricType] = useState('');
  const [value, setValue] = useState('');
  const [unit, setUnit] = useState('');
  // Which extraction pipeline an uploaded document goes through on the
  // edge node -- see collection.py's process_document docstring: only
  // "grocery_receipt" dispatches to the grocery pipeline, every other
  // metric_type (the existing 'photo'/'document' values below) runs
  // the original lab pipeline, so this is purely additive and doesn't
  // change any existing upload's behavior.
  const [documentType, setDocumentType] = useState<'lab' | 'grocery' | 'restaurant'>('lab');
  const [devicePubkeyToDeregister, setDevicePubkeyToDeregister] = useState('');
  const [wellnessAnalysis, setWellnessAnalysis] = useState<string | null>(null);
  const [voiceRecording, setVoiceRecording] = useState<VoiceRecording | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState<RecordedAudio | null>(null);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const [voiceResponse, setVoiceResponse] = useState('');
  const [chatMessage, setChatMessage] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [chatQuestion, setChatQuestion] = useState('');
  const [chatAnswer, setChatAnswer] = useState('');
  const [flaggedByRecord, setFlaggedByRecord] = useState<Record<string, FlaggedLabValue[]>>({});
  const [askResponses, setAskResponses] = useState<Record<string, string>>({});
  const [askingKey, setAskingKey] = useState<string | null>(null);
  const [viewingRecord, setViewingRecord] = useState<HealthRecord | null>(null);
  const [viewingContent, setViewingContent] = useState<RecordContent | null>(null);
  const [viewingBusy, setViewingBusy] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [viewingObjectUrl, setViewingObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!viewingContent) {
      setViewingObjectUrl(null);
      return undefined;
    }
    // Object URLs (rather than a hand-built base64 data: URI) avoid
    // re-encoding potentially several MB of image bytes just to render
    // a preview — this is the same in-memory ArrayBuffer already
    // fetched, just given a URL the <img> tag can point at.
    const url = URL.createObjectURL(new Blob([viewingContent.data], { type: viewingContent.contentType }));
    setViewingObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [viewingContent]);

  useEffect(() => {
    setPairingBundle(getPairingBundle());
  }, []);

  const api = useMemo(() => (pairingBundle ? new DomiHealthApi(pairingBundle) : null), [pairingBundle]);

  const refreshRecords = async () => {
    if (!api) return;
    try {
      // getDashboard's records are a superset of listRecords' — using it
      // here means every existing call site (unlock, add measurement,
      // upload, claims, restore) keeps flagged items current for free,
      // without each needing its own separate dashboard refresh.
      const dashboard = await api.getDashboard(userId);
      setRecords(dashboard.records);
      setFlaggedByRecord(dashboard.flagged_items);
    } catch (err) {
      enqueueSnackbar(describeError(err), { variant: 'error' });
    }
  };

  const flaggedItemsFlat = useMemo(() => {
    const flat: { recordId: string; index: number; item: FlaggedLabValue }[] = [];
    Object.entries(flaggedByRecord).forEach(([recordId, items]) => {
      items.forEach((item, index) => flat.push({ recordId, index, item }));
    });
    return flat;
  }, [flaggedByRecord]);

  const handleAskAboutItem = async (recordId: string, item: FlaggedLabValue, index: number) => {
    if (!api) return;
    const key = `${recordId}-${index}`;
    setAskingKey(key);
    try {
      const label = item.name.replace(/_/g, ' ');
      // Localized to the app's current UI language, not hardcoded
      // English: the edge node picks its reply language from the
      // question's own text (see DHealth's build_chat_prompt), so a
      // Chinese-UI user asking this got an English reply back until
      // this question was actually worded in Chinese too.
      const question = strings.formatString(
        strings.domiHealth.askAboutItemQuestion,
        label,
        item.value,
        item.unit,
      ) as string;
      // skipMeasurementCheck=true: this question is always about an
      // already-flagged value, never a request to log something new --
      // see api.chat's doc comment for the real bug this prevents
      // (a Chinese question here once got misclassified as a request
      // to record a different metric entirely).
      const { response } = await api.chat(userId, question, false, true);
      setAskResponses((prev) => ({ ...prev, [key]: response }));
    } catch (err) {
      enqueueSnackbar(describeError(err), { variant: 'error' });
    } finally {
      setAskingKey(null);
    }
  };

  const handlePaired = (bundle: PairingBundle) => {
    savePairingBundle(bundle);
    setPairingBundle(bundle);
    setPairingDialogOpen(false);
    setUnlocked(false);
    // A newly-paired edge node has never seen this wallet's derived
    // passphrase fail — always worth a fresh auto-unlock attempt rather
    // than carrying over a previous edge node's fallback state.
    setAutoUnlockFailed(false);
  };

  const handleForget = () => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(strings.domiHealth.forgetEdgeNodeConfirm)) return;
    clearPairingBundle();
    setPairingBundle(null);
    setUnlocked(false);
    setRecords([]);
    setWellnessAnalysis(null);
    setFlaggedByRecord({});
    setAskResponses({});
  };

  // Auto-unlock with a passphrase derived from the wallet itself --
  // real bug/friction this replaces: asking the user to invent and
  // remember a *second* passphrase, entirely separate from their
  // wallet, purely to open their own health data. The wallet's Ed25519
  // signature over a fixed message is deterministic (RFC 8032), so it
  // reproduces the exact same passphrase every time with no state to
  // store client-side.
  //
  // Falls back to the old manual-entry form only if this fails with
  // IncorrectPassphraseError -- an account whose local database was
  // already encrypted with a user-typed passphrase *before* this
  // change shipped has no way to be opened by the newly-derived one,
  // and must not be silently stranded. Any other failure (edge node
  // unreachable, locked out some other way) surfaces as a snackbar via
  // describeError, same as before.
  useEffect(() => {
    if (!api || !wallet || unlocked || autoUnlocking || autoUnlockFailed) return;
    let cancelled = false;
    setAutoUnlocking(true);
    (async () => {
      try {
        const derivedPassphrase = await deriveUnlockPassphrase(wallet);
        await api.unlock(userId, derivedPassphrase);
        if (cancelled) return;
        setUnlocked(true);
        await refreshRecords();
      } catch (err) {
        if (cancelled) return;
        if (err instanceof IncorrectPassphraseError) {
          setAutoUnlockFailed(true);
        } else {
          enqueueSnackbar(describeError(err), { variant: 'error' });
        }
      } finally {
        if (!cancelled) setAutoUnlocking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, wallet, unlocked, autoUnlockFailed]);

  const handleManualUnlock = async () => {
    if (!api) return;
    setBusy(true);
    try {
      await api.unlock(userId, manualPassphrase);
      setUnlocked(true);
      setManualPassphrase('');
      setAutoUnlockFailed(false);
      // One-time migration off the old manual passphrase: now that
      // it's proven to unlock this account, switch the database over
      // to the wallet-derived one so every unlock after this is
      // automatic -- see storage.rekey_user_db's docstring. Best-
      // effort: a failure here must never block the user from using
      // the app they just unlocked, it just means they'll hit this
      // same manual fallback again next time instead of silently
      // losing access to anything.
      if (wallet) {
        try {
          const derivedPassphrase = await deriveUnlockPassphrase(wallet);
          await api.rekey(userId, derivedPassphrase);
        } catch (rekeyErr) {
          // eslint-disable-next-line no-console
          console.error('DomiHealth passphrase migration failed (will retry next unlock):', rekeyErr);
        }
      }
      await refreshRecords();
    } catch (err) {
      enqueueSnackbar(describeError(err), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleLock = async () => {
    if (!api) return;
    try {
      await api.lock(userId);
    } catch (err) {
      // Locking is best-effort from the UI's perspective — the session
      // will also idle-timeout on its own on the edge node.
    }
    setUnlocked(false);
    setRecords([]);
    setWellnessAnalysis(null);
    setFlaggedByRecord({});
    setAskResponses({});
  };

  const handleAddMeasurement = async () => {
    if (!api || !metricType || !value) return;
    setBusy(true);
    try {
      await api.addMeasurement(userId, metricType, value, unit || undefined, 'record');
      setMetricType('');
      setValue('');
      setUnit('');
      await refreshRecords();
    } catch (err) {
      enqueueSnackbar(describeError(err), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleUploadPhoto = async () => {
    if (!api) return;
    setBusy(true);
    try {
      const fileUri = await takePicture();
      await api.uploadDocument(userId, metricTypeForUpload(documentType, 'photo'), fileUri);
      enqueueSnackbar(uploadedMessageForDocumentType(documentType), { variant: 'success' });
      await refreshRecords();
    } catch (err) {
      // A duplicate-upload rejection isn't really a failure -- the file
      // is already there -- so it gets the gentler 'info' variant
      // instead of the alarming 'error' one every other upload failure
      // uses.
      const isDuplicate = err instanceof DomiHealthApiError && err.status === 409;
      enqueueSnackbar(err instanceof DomiHealthApiError ? describeError(err) : strings.domiHealth.cameraError, {
        variant: isDuplicate ? 'info' : 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleUploadFromStorage = async () => {
    if (!api) return;
    setBusy(true);
    try {
      const picked = await pickFileForUpload();
      if (!picked) return; // user cancelled — not an error
      await api.uploadDocumentChunked(
        userId,
        metricTypeForUpload(documentType, 'document'),
        picked.data,
        picked.name,
        picked.mediaType,
      );
      enqueueSnackbar(uploadedMessageForDocumentType(documentType), { variant: 'success' });
      await refreshRecords();
    } catch (err) {
      const isDuplicate = err instanceof DomiHealthApiError && err.status === 409;
      enqueueSnackbar(err instanceof DomiHealthApiError ? describeError(err) : strings.domiHealth.filePickerError, {
        variant: isDuplicate ? 'info' : 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleClaimRewards = async () => {
    if (!api || !wallet) return;
    setBusy(true);
    try {
      const userWallet = wallet.publicKey.toString();
      const { claims, skipped, analysis } = await api.buildClaims(userId, userWallet);
      if (analysis) {
        setWellnessAnalysis(analysis);
      }

      if (claims.length === 0 && skipped.length === 0) {
        enqueueSnackbar(strings.domiHealth.noClaimsAvailable, { variant: 'info' });
        return;
      }

      let submittedCount = 0;
      for (const claim of claims) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const signature = await coSignAndSubmit(connection, wallet, claim.transaction);
          // eslint-disable-next-line no-await-in-loop
          await api.confirmClaim(userId, claim.record_id, signature);
          submittedCount += 1;
        } catch (err) {
          enqueueSnackbar(
            strings.formatString(strings.domiHealth.claimFailed, err instanceof Error ? err.message : String(err)),
            { variant: 'error' },
          );
          // One failed claim shouldn't stop the rest from being tried —
          // each is an independent transaction/record.
        }
      }

      if (submittedCount > 0) {
        enqueueSnackbar(strings.formatString(strings.domiHealth.claimsSubmitted, submittedCount), {
          variant: 'success',
        });
      }
      if (skipped.length > 0) {
        enqueueSnackbar(
          strings.formatString(
            strings.domiHealth.claimsSkipped,
            skipped.length,
            skipped.map((s) => s.reason).join('; '),
          ),
          { variant: 'warning' },
        );
      }
      await refreshRecords();
    } catch (err) {
      enqueueSnackbar(describeError(err), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleBackup = async () => {
    if (!api || !wallet) return;
    setBusy(true);
    try {
      const userWallet = wallet.publicKey.toString();
      const signatureHex = await deriveBackupSignatureHex(wallet);
      const { transaction } = await api.createBackup(userId, userWallet, signatureHex);
      await coSignAndSubmit(connection, wallet, transaction);
      enqueueSnackbar(strings.domiHealth.backupComplete, { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(describeError(err), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = async () => {
    if (!api || !wallet) return;
    setBusy(true);
    try {
      const userWallet = wallet.publicKey.toString();
      const signatureHex = await deriveBackupSignatureHex(wallet);
      const { inserted } = await api.restoreBackup(userId, userWallet, signatureHex);
      enqueueSnackbar(strings.formatString(strings.domiHealth.restoreComplete, inserted), { variant: 'success' });
      await refreshRecords();
    } catch (err) {
      if (err instanceof DomiHealthApiError && err.status === 404) {
        enqueueSnackbar(strings.domiHealth.restoreNoBackupFound, { variant: 'info' });
      } else {
        enqueueSnackbar(describeError(err), { variant: 'error' });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDeregister = async () => {
    if (!api || !wallet || !devicePubkeyToDeregister) return;
    setBusy(true);
    try {
      const userWallet = wallet.publicKey.toString();
      const { transaction } = await api.deregisterDevice(userWallet, devicePubkeyToDeregister.trim());
      // No device signature to preserve here at all — build_deregistration
      // (chain.py) deliberately signs with the user wallet only, since
      // the whole point is working without the lost device present.
      // coSignAndSubmit's partialSign still applies correctly either way.
      await coSignAndSubmit(connection, wallet, transaction);
      enqueueSnackbar(strings.domiHealth.deregisterComplete, { variant: 'success' });
      setDevicePubkeyToDeregister('');
    } catch (err) {
      enqueueSnackbar(describeError(err), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleStartRecording = async () => {
    try {
      const recording = new VoiceRecording();
      await recording.start();
      setVoiceRecording(recording);
      setIsRecording(true);
      setRecordedAudio(null);
      setVoiceTranscript('');
      setVoiceResponse('');
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : strings.domiHealth.voiceNativeOnly, {
        variant: 'error',
      });
    }
  };

  const handleStopRecording = async () => {
    if (!voiceRecording) return;
    const audio = await voiceRecording.stop();
    setRecordedAudio(audio);
    setIsRecording(false);
    setVoiceRecording(null);
  };

  // Branches on which shape voiceRecorder.ts's VoiceRecording produced —
  // a native file:// path, or (in the browser fallback) in-memory bytes
  // from MediaRecorder — since the edge node needs a different upload
  // call for each (transcribeAudio vs. transcribeAudioBytes).
  const transcribeRecordedAudio = (audio: RecordedAudio) => {
    if (!api) throw new Error('not paired');
    return 'fileUri' in audio
      ? api.transcribeAudio(userId, audio.fileUri)
      : api.transcribeAudioBytes(userId, audio.data, audio.mediaType);
  };

  const handleAskAssistant = async () => {
    if (!api || !recordedAudio) return;
    setVoiceBusy(true);
    try {
      const { text } = await transcribeRecordedAudio(recordedAudio);
      setVoiceTranscript(text);

      // voice=true: this reply gets read aloud via TTS below, so the
      // edge node keeps it to one or two short sentences instead of the
      // fuller answer the typed chat box and "Ask about this" expect.
      const { response } = await api.chat(userId, text, true);
      setVoiceResponse(response);

      const audio = await api.synthesizeSpeech(userId, response);
      await playSynthesizedAudio(audio.data, audio.contentType);
    } catch (err) {
      enqueueSnackbar(describeError(err), { variant: 'error' });
    } finally {
      setVoiceBusy(false);
      setRecordedAudio(null);
    }
  };

  const handleSaveAsNote = async () => {
    if (!api || !recordedAudio) return;
    setVoiceBusy(true);
    try {
      const { text } = await transcribeRecordedAudio(recordedAudio);
      setMetricType('voice_note');
      setValue(text);
      setUnit('');
      enqueueSnackbar(strings.domiHealth.voiceNoteTranscribed, { variant: 'info' });
    } catch (err) {
      enqueueSnackbar(describeError(err), { variant: 'error' });
    } finally {
      setVoiceBusy(false);
      setRecordedAudio(null);
    }
  };

  const handleSendChatMessage = async () => {
    const message = chatMessage.trim();
    if (!api || !message) return;
    setChatBusy(true);
    try {
      const { response } = await api.chat(userId, message);
      setChatQuestion(message);
      setChatAnswer(response);
      setChatMessage('');
    } catch (err) {
      enqueueSnackbar(describeError(err), { variant: 'error' });
    } finally {
      setChatBusy(false);
    }
  };

  const handleOpenRecordViewer = async (record: HealthRecord) => {
    if (!api || !hasFileContent(record)) return;
    setViewingRecord(record);
    setViewingContent(null);
    setViewingBusy(true);
    try {
      const content = await api.getRecordContent(userId, record.id);
      setViewingContent(content);
    } catch (err) {
      enqueueSnackbar(describeError(err), { variant: 'error' });
      setViewingRecord(null);
    } finally {
      setViewingBusy(false);
    }
  };

  const handleCloseRecordViewer = () => {
    setViewingRecord(null);
    setViewingContent(null);
  };

  const handleDownloadRecord = async () => {
    if (!viewingRecord || !viewingContent) return;
    setDownloadBusy(true);
    try {
      const fileName = viewingRecord.payload.filename || `${viewingRecord.metric_type}-${viewingRecord.id}`;
      const saved = await saveRecordContentToDevice(fileName, viewingContent.data);
      enqueueSnackbar(strings.formatString(strings.domiHealth.viewerDownloadComplete, saved.filename) as string, {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(err instanceof DomiHealthApiError ? describeError(err) : strings.domiHealth.viewerDownloadFailed, {
        variant: 'error',
      });
    } finally {
      setDownloadBusy(false);
    }
  };

  if (!pairingBundle) {
    return (
      <Box p={2}>
        <Card>
          <CardContent>
            <Typography variant="h6">{strings.domiHealth.notPairedTitle}</Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8, marginBottom: 16 }}>
              {strings.domiHealth.notPairedDescription}
            </Typography>
            <Button variant="contained" color="primary" onClick={() => setPairingDialogOpen(true)}>
              {strings.domiHealth.pairButton}
            </Button>
          </CardContent>
        </Card>
        <PairingDialog
          open={pairingDialogOpen}
          onClose={() => setPairingDialogOpen(false)}
          onPaired={handlePaired}
        />
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Card style={{ marginBottom: 16 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2">
              {strings.formatString(strings.domiHealth.pairedWith, `${pairingBundle.host}:${pairingBundle.port}`)}
            </Typography>
            <Button size="small" onClick={handleForget}>
              {strings.domiHealth.forgetEdgeNodeButton}
            </Button>
          </Box>
          <Typography variant="caption" color="textSecondary">
            {strings.domiHealth.insecureConnectionNotice}
          </Typography>
        </CardContent>
      </Card>

      {!unlocked ? (
        <Card>
          <CardContent>
            <Typography variant="h6">{strings.domiHealth.unlockTitle}</Typography>
            {!autoUnlockFailed ? (
              <Box display="flex" alignItems="center" mt={2}>
                <CircularProgress size={20} style={{ marginRight: 12 }} />
                <Typography variant="body2" color="textSecondary">
                  {strings.domiHealth.autoUnlocking}
                </Typography>
              </Box>
            ) : (
              <>
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                  {strings.domiHealth.passphraseFallbackHint}
                </Typography>
                <TextField
                  label={strings.domiHealth.passphraseLabel}
                  type="password"
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  value={manualPassphrase}
                  onChange={(e) => setManualPassphrase(e.target.value)}
                />
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!manualPassphrase || busy}
                  onClick={handleManualUnlock}
                >
                  {busy ? <CircularProgress size={20} /> : strings.domiHealth.unlockButton}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <Card style={{ marginBottom: 16 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">{strings.domiHealth.dashboardSectionTitle}</Typography>
                <Button size="small" onClick={refreshRecords}>
                  {strings.domiHealth.dashboardRefreshButton}
                </Button>
              </Box>
              {flaggedItemsFlat.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  {strings.domiHealth.dashboardNoFlaggedItems}
                </Typography>
              ) : (
                <>
                  <Typography variant="body2" style={{ color: '#f9a825', marginTop: 4, marginBottom: 8 }}>
                    {strings.formatString(strings.domiHealth.dashboardFlaggedItemsCount, flaggedItemsFlat.length)}
                  </Typography>
                  <List>
                    {flaggedItemsFlat.map(({ recordId, index, item }) => {
                      const key = `${recordId}-${index}`;
                      return (
                        <ListItem key={key} divider style={{ display: 'block', paddingTop: 12, paddingBottom: 12 }}>
                          <ListItemText
                            primary={`${item.name.replace(/_/g, ' ')}: ${item.value} ${item.unit}`}
                            secondary={`${strings.domiHealth.dashboardTypicalRangeLabel}: ${item.typical_range}`}
                          />
                          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
                            {item.note}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={askingKey === key}
                            onClick={() => handleAskAboutItem(recordId, item, index)}
                          >
                            {askingKey === key ? strings.domiHealth.dashboardAsking : strings.domiHealth.dashboardAskAboutButton}
                          </Button>
                          {askResponses[key] && (
                            <Typography variant="body2" style={{ marginTop: 8 }}>
                              {askResponses[key]}
                            </Typography>
                          )}
                        </ListItem>
                      );
                    })}
                  </List>
                </>
              )}
            </CardContent>
          </Card>

          <Card style={{ marginBottom: 16 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">{strings.domiHealth.recordsTitle}</Typography>
                <Button size="small" onClick={handleLock}>
                  {strings.domiHealth.lockButton}
                </Button>
              </Box>
              <List>
                {records.length === 0 && (
                  <ListItem>
                    <ListItemText primary={strings.domiHealth.noRecords} />
                  </ListItem>
                )}
                {records.map((record) => {
                  const patientInfo = record.payload.patient_info;
                  const identityWarnings = record.payload.identity_warnings;
                  const reportDate = record.payload.report_date;
                  const labValues = record.payload.lab_values;
                  // grocery_receipt/restaurant_receipt counterparts to
                  // report_date/lab_values above (document record) and
                  // isFlaggedValue below (per-item record) -- see
                  // receipts.py.
                  const purchaseDate = record.payload.purchase_date;
                  const isReceiptItem = record.metric_type === 'grocery_item' || record.metric_type === 'restaurant_item';
                  // Only set on the individual per-lab-value records
                  // collection.import_document writes alongside a
                  // document (one per extracted value) — not on the
                  // document record itself.
                  const isFlaggedValue = record.payload.flagged === true;
                  // Only ever set on a document record, right after
                  // upload — OCR/lab-value extraction runs in the
                  // background and can take several minutes for a real
                  // multi-page report (confirmed live: ~260s), so the
                  // record briefly exists with none of its analysis
                  // filled in yet. Distinct from the anchoredChip below
                  // (that's reward-claim status, an unrelated concept).
                  const isProcessing = record.payload.processing_status === 'pending';
                  return (
                    <ListItem
                      key={record.id}
                      divider
                      button={hasFileContent(record)}
                      onClick={hasFileContent(record) ? () => handleOpenRecordViewer(record) : undefined}
                      style={{ display: 'block', paddingTop: 12, paddingBottom: 12 }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <ListItemText
                          primary={
                            isReceiptItem
                              ? `${record.payload.name} (${record.action})`
                              : `${record.metric_type} (${record.action})`
                          }
                          secondary={`${formatRecord(record)} — ${record.recorded_at}`}
                        />
                        <Chip
                          size="small"
                          label={record.anchored_at ? strings.domiHealth.anchoredChip : strings.domiHealth.pendingChip}
                          color={record.anchored_at ? 'primary' : 'default'}
                        />
                      </Box>
                      {isProcessing && (
                        <Box display="flex" alignItems="center" style={{ gap: 6, marginTop: 4 }}>
                          <CircularProgress size={12} />
                          <Typography variant="caption" color="textSecondary">
                            {strings.domiHealth.processingDocument}
                          </Typography>
                        </Box>
                      )}
                      {reportDate && (
                        <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                          {strings.domiHealth.reportDateLabel}: {reportDate}
                        </Typography>
                      )}
                      {purchaseDate && (
                        <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                          {strings.domiHealth.purchaseDateLabel}: {purchaseDate}
                        </Typography>
                      )}
                      {record.payload.grocery_items && record.payload.grocery_items.length > 0 && (
                        <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                          {strings.formatString(strings.domiHealth.groceryItemsCount, record.payload.grocery_items.length)}
                        </Typography>
                      )}
                      {record.payload.restaurant_items && record.payload.restaurant_items.length > 0 && (
                        <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                          {strings.formatString(
                            strings.domiHealth.restaurantItemsCount,
                            record.payload.restaurant_items.length,
                          )}
                        </Typography>
                      )}
                      {patientInfo && (
                        <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                          {strings.domiHealth.patientInfoLabel}: {formatPatientInfo(patientInfo)}
                        </Typography>
                      )}
                      {labValues && labValues.length > 0 && (
                        <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                          {strings.formatString(strings.domiHealth.labValuesCount, labValues.length)}
                        </Typography>
                      )}
                      {isFlaggedValue && (
                        <Typography variant="caption" style={{ color: '#f9a825', display: 'block' }}>
                          {strings.domiHealth.dashboardTypicalRangeLabel}: {record.payload.typical_range}
                        </Typography>
                      )}
                      {identityWarnings?.map((warning, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Typography key={index} variant="caption" color="error" style={{ display: 'block' }}>
                          ⚠ {warning}
                        </Typography>
                      ))}
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>

          <Dialog open={Boolean(viewingRecord)} onClose={handleCloseRecordViewer} maxWidth="sm" fullWidth>
            <DialogTitle>{viewingRecord?.payload.filename || viewingRecord?.metric_type}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                {strings.domiHealth.viewerUploadedAtLabel}: {viewingRecord?.recorded_at}
              </Typography>
              {viewingRecord?.payload.report_date && (
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                  {strings.domiHealth.reportDateLabel}: {viewingRecord.payload.report_date}
                </Typography>
              )}
              {viewingRecord?.payload.patient_info && (
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                  {strings.domiHealth.patientInfoLabel}: {formatPatientInfo(viewingRecord.payload.patient_info)}
                </Typography>
              )}
              {viewingRecord?.payload.identity_warnings?.map((warning, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Typography key={index} variant="body2" color="error" style={{ marginBottom: 8 }}>
                  ⚠ {warning}
                </Typography>
              ))}
              {viewingRecord?.payload.lab_values && viewingRecord.payload.lab_values.length > 0 && (
                <Box style={{ marginBottom: 8 }}>
                  <Typography variant="body2" color="textSecondary">
                    {strings.domiHealth.labValuesLabel}:
                  </Typography>
                  <List dense disablePadding>
                    {viewingRecord.payload.lab_values.map((item, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <ListItem key={index} disableGutters style={{ paddingTop: 0, paddingBottom: 0 }}>
                        <ListItemText
                          primary={`${item.name.replace(/_/g, ' ')}: ${item.value} ${item.unit}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
              {viewingBusy && (
                <Box display="flex" justifyContent="center" padding={2}>
                  <CircularProgress />
                </Box>
              )}
              {!viewingBusy && viewingContent && viewingContent.contentType.startsWith('image/') && (
                <img
                  src={viewingObjectUrl ?? undefined}
                  alt={viewingRecord?.payload.filename || viewingRecord?.metric_type || ''}
                  style={{ maxWidth: '100%', display: 'block', margin: '0 auto' }}
                />
              )}
              {!viewingBusy && viewingContent && !viewingContent.contentType.startsWith('image/') && (
                <Typography variant="body2" color="textSecondary">
                  {strings.formatString(strings.domiHealth.viewerPreviewUnavailable, viewingContent.contentType)}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseRecordViewer}>{strings.domiHealth.viewerCloseButton}</Button>
              <Button color="primary" disabled={!viewingContent || downloadBusy} onClick={handleDownloadRecord}>
                {downloadBusy ? strings.domiHealth.viewerDownloading : strings.domiHealth.viewerDownloadButton}
              </Button>
            </DialogActions>
          </Dialog>

          {wellnessAnalysis && (
            <Card style={{ marginBottom: 16 }}>
              <CardContent>
                <Typography variant="subtitle1">{strings.domiHealth.wellnessAnalysisTitle}</Typography>
                <Typography variant="body2">{wellnessAnalysis}</Typography>
              </CardContent>
            </Card>
          )}

          <Card style={{ marginBottom: 16 }}>
            <CardContent>
              <Typography variant="h6">{strings.domiHealth.addMeasurementTitle}</Typography>
              <TextField
                label={strings.domiHealth.metricTypeLabel}
                fullWidth
                variant="outlined"
                margin="normal"
                value={metricType}
                onChange={(e) => setMetricType(e.target.value)}
              />
              <TextField
                label={strings.domiHealth.valueLabel}
                fullWidth
                variant="outlined"
                margin="normal"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
              <TextField
                label={strings.domiHealth.unitLabel}
                fullWidth
                variant="outlined"
                margin="normal"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
              />
              <Button
                variant="contained"
                color="primary"
                disabled={!metricType || !value || busy}
                onClick={handleAddMeasurement}
              >
                {busy ? <CircularProgress size={20} /> : strings.domiHealth.saveButton}
              </Button>
            </CardContent>
          </Card>

          <Card style={{ marginBottom: 16 }}>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" style={{ marginBottom: 4 }}>
                {strings.domiHealth.documentTypeLabel}
              </Typography>
              <Box display="flex" style={{ gap: 8, marginBottom: 12 }}>
                <Button
                  variant={documentType === 'lab' ? 'contained' : 'outlined'}
                  color={documentType === 'lab' ? 'primary' : 'default'}
                  size="small"
                  onClick={() => setDocumentType('lab')}
                >
                  {strings.domiHealth.documentTypeLab}
                </Button>
                <Button
                  variant={documentType === 'grocery' ? 'contained' : 'outlined'}
                  color={documentType === 'grocery' ? 'primary' : 'default'}
                  size="small"
                  onClick={() => setDocumentType('grocery')}
                >
                  {strings.domiHealth.documentTypeGrocery}
                </Button>
                <Button
                  variant={documentType === 'restaurant' ? 'contained' : 'outlined'}
                  color={documentType === 'restaurant' ? 'primary' : 'default'}
                  size="small"
                  onClick={() => setDocumentType('restaurant')}
                >
                  {strings.domiHealth.documentTypeRestaurant}
                </Button>
              </Box>
              <Button
                variant="outlined"
                fullWidth
                disabled={busy}
                onClick={handleUploadPhoto}
                style={{ marginBottom: 8 }}
              >
                {busy ? strings.domiHealth.uploadingPhoto : strings.domiHealth.uploadPhotoButton}
              </Button>
              <Button variant="outlined" fullWidth disabled={busy} onClick={handleUploadFromStorage}>
                {busy ? strings.domiHealth.uploadingFile : strings.domiHealth.uploadFromStorageButton}
              </Button>
            </CardContent>
          </Card>

          <Card style={{ marginBottom: 16 }}>
            <CardContent>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                disabled={busy}
                onClick={handleClaimRewards}
                style={{ marginBottom: 8 }}
              >
                {busy ? strings.domiHealth.claimingRewards : strings.domiHealth.claimRewardsButton}
              </Button>
            </CardContent>
          </Card>

          <Card style={{ marginBottom: 16 }}>
            <CardContent>
              <Box display="flex" style={{ gap: 8 }}>
                <Button variant="outlined" fullWidth disabled={busy} onClick={handleBackup}>
                  {busy ? strings.domiHealth.backingUp : strings.domiHealth.backupButton}
                </Button>
                <Button variant="outlined" fullWidth disabled={busy} onClick={handleRestore}>
                  {busy ? strings.domiHealth.restoring : strings.domiHealth.restoreButton}
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card style={{ marginBottom: 16 }}>
            <CardContent>
              <Typography variant="h6">{strings.domiHealth.chatSectionTitle}</Typography>
              <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
                {strings.domiHealth.chatSectionDescription}
              </Typography>
              <TextField
                label={strings.domiHealth.chatInputLabel}
                fullWidth
                multiline
                variant="outlined"
                margin="normal"
                value={chatMessage}
                disabled={chatBusy}
                onChange={(e) => setChatMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendChatMessage();
                  }
                }}
              />
              <Button
                variant="contained"
                color="primary"
                fullWidth
                disabled={!chatMessage.trim() || chatBusy}
                onClick={handleSendChatMessage}
              >
                {chatBusy ? <CircularProgress size={20} /> : strings.domiHealth.chatSendButton}
              </Button>
              {chatQuestion && (
                <Typography variant="body2" style={{ marginTop: 12 }}>
                  <strong>{strings.domiHealth.chatYouLabel}:</strong> {chatQuestion}
                </Typography>
              )}
              {chatAnswer && (
                <Typography variant="body2" style={{ marginTop: 4 }}>
                  <strong>{strings.domiHealth.chatAssistantLabel}:</strong> {chatAnswer}
                </Typography>
              )}
            </CardContent>
          </Card>

          <Card style={{ marginBottom: 16 }}>
            <CardContent>
              <Typography variant="h6">{strings.domiHealth.voiceSectionTitle}</Typography>
              <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
                {strings.domiHealth.voiceSectionDescription}
              </Typography>

              {!isRecording && !recordedAudio && (
                <Button variant="contained" color="primary" fullWidth disabled={voiceBusy} onClick={handleStartRecording}>
                  {strings.domiHealth.startRecordingButton}
                </Button>
              )}

              {isRecording && (
                <Button variant="contained" color="secondary" fullWidth onClick={handleStopRecording}>
                  {strings.domiHealth.stopRecordingButton}
                </Button>
              )}

              {!isRecording && recordedAudio && (
                <Box display="flex" style={{ gap: 8 }}>
                  <Button variant="outlined" fullWidth disabled={voiceBusy} onClick={handleAskAssistant}>
                    {voiceBusy ? strings.domiHealth.processingVoice : strings.domiHealth.askAssistantButton}
                  </Button>
                  <Button variant="outlined" fullWidth disabled={voiceBusy} onClick={handleSaveAsNote}>
                    {voiceBusy ? strings.domiHealth.processingVoice : strings.domiHealth.saveAsNoteButton}
                  </Button>
                </Box>
              )}

              {voiceTranscript && (
                <Typography variant="body2" style={{ marginTop: 12 }}>
                  <strong>{strings.domiHealth.voiceTranscriptLabel}:</strong> {voiceTranscript}
                </Typography>
              )}
              {voiceResponse && (
                <Typography variant="body2" style={{ marginTop: 4 }}>
                  <strong>{strings.domiHealth.voiceResponseLabel}:</strong> {voiceResponse}
                </Typography>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Card>
        <CardContent>
          <Typography variant="subtitle1">{strings.domiHealth.deregisterSectionTitle}</Typography>
          <Typography variant="caption" color="textSecondary">
            {strings.domiHealth.deregisterSectionDescription}
          </Typography>
          <TextField
            label={strings.domiHealth.devicePubkeyLabel}
            fullWidth
            variant="outlined"
            margin="normal"
            value={devicePubkeyToDeregister}
            onChange={(e) => setDevicePubkeyToDeregister(e.target.value)}
          />
          <Button
            variant="outlined"
            disabled={!devicePubkeyToDeregister || busy}
            onClick={handleDeregister}
          >
            {busy ? strings.domiHealth.deregistering : strings.domiHealth.deregisterButton}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}
